import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Ghost, Pencil, ArrowUpRight, Merge, RefreshCw, Search,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import usePersistentState from "@/hooks/usePersistentState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GhostItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = usePersistentState("ghost-items:search", "");

  // Rename state
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renameStem, setRenameStem] = useState("");
  const [saving, setSaving] = useState(false);

  // Promote state
  const [promoteTarget, setPromoteTarget] = useState(null);

  // Merge state
  const [mergeTarget, setMergeTarget] = useState(null);
  const [qbItems, setQbItems] = useState([]);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeQuery, setMergeQuery] = useState("");

  const fetchItems = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/ghost-items`, { withCredentials: true });
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load ghost items");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openRename = (item) => {
    setRenameTarget(item);
    setRenameTitle(item.title || "");
    setRenameStem(item.stem || "");
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/ghost-items/${renameTarget.id}`,
        { title: renameTitle, stem: renameStem },
        { withCredentials: true });
      toast.success("Ghost item renamed");
      setRenameTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  const submitPromote = async () => {
    if (!promoteTarget) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ghost-items/${promoteTarget.id}/promote`, {},
        { withCredentials: true });
      toast.success(`"${promoteTarget.title}" promoted to Question Bank`);
      setPromoteTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Promote failed");
    } finally {
      setSaving(false);
    }
  };

  const openMerge = async (item) => {
    setMergeTarget(item);
    setMergeTargetId("");
    setMergeQuery("");
    // Load QBank items (ghost excluded by default)
    try {
      const { data } = await axios.get(`${API}/items`, { withCredentials: true });
      setQbItems(data || []);
    } catch (err) {
      toast.error("Failed to load Question Bank items");
    }
  };

  const submitMerge = async () => {
    if (!mergeTarget || !mergeTargetId) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/ghost-items/${mergeTarget.id}/merge`,
        { target_item_id: mergeTargetId },
        { withCredentials: true });
      toast.success(`Merged — ${data.response_rows_migrated} response rows moved`);
      setMergeTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = items.filter((it) =>
    !search.trim() ||
    (it.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (it.id || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredQb = qbItems.filter((it) =>
    !mergeQuery.trim() ||
    (it.title || "").toLowerCase().includes(mergeQuery.toLowerCase()) ||
    (it.id || "").toLowerCase().includes(mergeQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ghost-items-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold" style={{ color: "var(--admin-text)" }}>
            Ghost Items
          </h1>
          <p className="text-sm font-body mt-1 max-w-2xl" style={{ color: "var(--admin-container-muted)" }}>
            Historical per-question item anchors auto-created when admins entered scores for item IDs that didn't exist in the Question Bank. Rename for clarity, or merge/promote them into the real Question Bank.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={fetchItems} disabled={refreshing}
          className="text-xs font-body gap-1.5"
          style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
          data-testid="refresh-ghost-btn"
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ghost items..."
          className="pl-8 h-9 text-sm"
          style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
          data-testid="ghost-search"
        />
      </div>

      {filteredItems.length === 0 ? (
        <div
          className="text-center py-16 rounded-xl border"
          style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
          data-testid="ghost-items-empty"
        >
          <Ghost className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--admin-container-muted)" }} />
          <p className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>
            {search ? "No matching ghost items" : "No ghost items"}
          </p>
          <p className="text-xs font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>
            {search
              ? "Try a different search."
              : "Ghost items appear here when admins enter historical per-question scores for unknown item IDs."}
          </p>
        </div>
      ) : (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
        >
          <div
            className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider font-body border-b"
            style={{ color: "var(--admin-container-muted)", borderColor: "var(--admin-container-border)" }}
          >
            <div className="col-span-5">Title</div>
            <div className="col-span-2">Source Assessment</div>
            <div className="col-span-2">Responses</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
            {filteredItems.map((it) => (
              <div
                key={it.id}
                className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors"
                data-testid={`ghost-row-${it.id}`}
              >
                <div className="col-span-5 min-w-0">
                  <div className="flex items-center gap-2">
                    <Ghost className="h-3.5 w-3.5 shrink-0" style={{ color: "#6366f1" }} />
                    <p className="text-sm font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>
                      {it.title || it.id}
                    </p>
                  </div>
                  <p className="text-[10px] font-body font-mono mt-0.5 truncate" style={{ color: "var(--admin-container-muted)" }}>
                    {it.id}
                  </p>
                </div>
                <div className="col-span-2 text-xs font-body truncate" style={{ color: "var(--admin-container-muted)" }}>
                  {it.assessment_type_name || "—"}
                </div>
                <div className="col-span-2">
                  <Badge
                    className="text-[10px] font-body"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--primary-color) 15%, transparent)",
                      color: "var(--primary-color)",
                      border: "1px solid var(--primary-color)",
                    }}
                  >
                    {it.response_count} response{it.response_count === 1 ? "" : "s"}
                  </Badge>
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1.5">
                  <Button
                    variant="outline" size="sm" onClick={() => openRename(it)}
                    className="text-[11px] font-body h-7 gap-1"
                    style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                    data-testid={`ghost-rename-${it.id}`}
                  >
                    <Pencil className="h-3 w-3" /> Rename
                  </Button>
                  <Button
                    variant="outline" size="sm" onClick={() => openMerge(it)}
                    className="text-[11px] font-body h-7 gap-1"
                    style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                    data-testid={`ghost-merge-${it.id}`}
                  >
                    <Merge className="h-3 w-3" /> Merge
                  </Button>
                  <Button
                    size="sm" onClick={() => setPromoteTarget(it)}
                    className="text-[11px] font-body h-7 gap-1"
                    style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                    data-testid={`ghost-promote-${it.id}`}
                  >
                    <ArrowUpRight className="h-3 w-3" /> Promote
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>
              Rename Ghost Item
            </DialogTitle>
            <DialogDescription className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>
              <code className="font-mono">{renameTarget?.id}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-body font-medium" style={{ color: "var(--admin-container-muted)" }}>Title</label>
              <Input
                value={renameTitle} onChange={(e) => setRenameTitle(e.target.value)}
                className="mt-1 text-sm"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="rename-title-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-body font-medium" style={{ color: "var(--admin-container-muted)" }}>Stem (optional)</label>
              <Input
                value={renameStem} onChange={(e) => setRenameStem(e.target.value)}
                className="mt-1 text-sm"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setRenameTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitRename} disabled={saving} className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="rename-save-btn">
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote confirm */}
      <Dialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>
              Promote to Question Bank
            </DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Promote <strong style={{ color: "var(--admin-container-text)" }}>{promoteTarget?.title}</strong> to a regular Question Bank item?<br /><br />
              • Item becomes visible in the Question Bank and counts in psychometrics.<br />
              • Existing responses are preserved unchanged.<br />
              • The item stays historical-shaped (item_type=external_item) — edit it in the Question Bank to flesh it out.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setPromoteTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitPromote} disabled={saving} className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="promote-confirm-btn">
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}Confirm Promote
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={!!mergeTarget} onOpenChange={(o) => !o && setMergeTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)", maxWidth: "640px" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>
              Merge into Question Bank Item
            </DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Pick a real Question Bank item to merge <strong style={{ color: "var(--admin-container-text)" }}>{mergeTarget?.title}</strong> into.
              All {mergeTarget?.response_count} response{mergeTarget?.response_count === 1 ? "" : "s"} will be rewritten to the target item, and this ghost will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
              <Input
                value={mergeQuery} onChange={(e) => setMergeQuery(e.target.value)}
                placeholder="Search Question Bank..."
                className="pl-8 h-9 text-sm"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="merge-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-lg divide-y"
              style={{ borderColor: "var(--admin-container-border)" }}>
              {filteredQb.length === 0 && (
                <p className="text-xs font-body text-center py-6" style={{ color: "var(--admin-container-muted)" }}>
                  No matching Question Bank items
                </p>
              )}
              {filteredQb.slice(0, 100).map((it) => (
                <button
                  key={it.id}
                  onClick={() => setMergeTargetId(it.id)}
                  className="w-full text-left px-3 py-2 hover:bg-white/[0.04] transition-colors"
                  style={{ backgroundColor: mergeTargetId === it.id ? "color-mix(in srgb, var(--primary-color) 10%, transparent)" : "transparent" }}
                  data-testid={`merge-option-${it.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full border-2 shrink-0"
                      style={{
                        borderColor: mergeTargetId === it.id ? "var(--primary-color)" : "var(--admin-container-muted)",
                        backgroundColor: mergeTargetId === it.id ? "var(--primary-color)" : "transparent",
                      }}
                    />
                    <p className="text-xs font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>
                      {it.title || it.id}
                    </p>
                    <Badge variant="outline" className="text-[9px] font-body shrink-0 ml-auto"
                      style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                      {it.item_type || "unknown"}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setMergeTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitMerge} disabled={saving || !mergeTargetId} className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="merge-confirm-btn">
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              <Merge className="h-3 w-3" /> Confirm Merge
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
